import * as LZString from 'lz-string';

import { Dictionary, isEqual } from "lodash";

import { CatalogItem } from "../catalog/interface/CatalogItem";
import { Form } from "./interface/Form";
import { FormRule } from "../rules/interface/rulesInterface";
import { Section } from "../../models/Section/Section";
import { orderAndComparePreferences } from '../../shared/components/organisms/PreferenceColumn/preferencesUtils';
import storage from "../../helpers/storage";
import FormLocales from '../../models/Form/FormLocales';

/**
 * Represents the structure of a form stored in local storage.
 *
 * @interface LocalStorageForm
 *
 * @property {boolean} isOriginalState - Indicates whether the form is in its original state (form data has not been modified). Used to check if the form was updated.
 * @property {Form} form - The form object.
 * @property {Section[]} sections - An array of sections within the form.
 * @property {Dictionary<CatalogItem[]>} fields - A dictionary mapping field names to catalog items.
 * @property {FormRule[]} rules - An array of rules associated with the form.
 */
export interface LocalStorageForm {
  form: Form;
  sections: Section[];
  fields: Dictionary<CatalogItem[]>;
  rules: FormRule[];
}

export const saveFormToLocalStorage = (formData: LocalStorageForm) => {
  try {
    const formId = formData.form.formId;

    const compressedData = compressForm(formData);

    storage.setAutosaveForm(formId, compressedData);
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.error("Failed to save form to local storage: Quota exceeded");
    }
  }
};

export const saveOriginalFormToLocalStorage = (formData: LocalStorageForm) => {
  try {
    const formId = formData.form.formId;

    const compressedData = compressForm(formData);

    storage.setOriginalForm(formId, compressedData);
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.error("Failed to save original form to local storage: Quota exceeded");
    }
  }
}

export const getOriginalFormFromLocalStorage = (formId: number): LocalStorageForm | null => {
  const originalForms = storage.getOriginalForms();
  const formData = originalForms ? JSON.parse(originalForms)[formId] : null;

  if (formData) {
    return decompressForm(formData);
  }

  return null;
}

export const getAutosavedFormFromLocalStorage = (formId: number): LocalStorageForm | null => {
  const autoSaveForms = storage.getAutosaveForms();
  const formData = autoSaveForms ? JSON.parse(autoSaveForms)[formId] : null;

  if (formData) {
    return decompressForm(formData);
  }

  return null;
};

export const decompressForm = (compressedData: string): LocalStorageForm => {
  const decompressedData = LZString.decompress(compressedData);
  if (!decompressedData) {
    throw new Error("Failed to decompress form data");
  }
  return JSON.parse(decompressedData) as LocalStorageForm;
}

export const compressForm = (formData: LocalStorageForm): string => {
  return LZString.compress(JSON.stringify(formData));
}

/**
 * Checks if the form has been changed by comparing the current form data with the original form data.
 *
 * @param currentForm - The current form data.
 * @param originalForm - The original form data.
 * @returns A boolean indicating whether the form has been changed.
 */
export const hasFormBeenChanged = (currentForm: LocalStorageForm, originalForm: LocalStorageForm): boolean => {

  const didFormContentChange = !areFormsEqual(currentForm.form, originalForm.form);
  if (isFormStructureEmpty(currentForm)) {
    return didFormContentChange;
  }
  const didFieldsChange = !areFieldsEqual(currentForm.fields, originalForm.fields);
  const didRulesChange = !areRulesEqual(currentForm.rules, originalForm.rules);
  const didSectionsChange = !areSectionsEqual(currentForm.sections, originalForm.sections);

  return didFieldsChange || didRulesChange || didSectionsChange || didFormContentChange;
};


// ---- COMPARISON HELPERS ----

/**
 * Checks if the form structure is empty: a form structure is empty if the form was just created:
 * - The form has no sections, rules, or fields.
 * - The only changes made were languages, users/co-owners or enable/disable.
 *
 * @param formData - The form data to be checked.
 * @returns A boolean indicating whether the form structure is empty.
 */
const isFormStructureEmpty = (formData: LocalStorageForm): boolean => {
  const onlyFieldList = Object.values(formData.fields)[0];
  return (
    formData.sections.length === 1 && // Has exactly one section
    (!formData.sections[0].formFields || formData.sections[0].formFields.length === 0) && // Section has no fields
    (!formData.rules || formData.rules.length === 0) && // No rules
    (!onlyFieldList || onlyFieldList.length === 0) // No fields
  );
};

const compareUserEntitiesByKey = (currentEntities: any[], originalEntities: any[], key: string) => {
  const currentEntitiesKeys = JSON.stringify(
    currentEntities.map(entity => entity[key]).sort((a, b) => String(a).localeCompare(String(b)))
  );
  const originalEntitiesKeys = JSON.stringify(
    originalEntities.map(entity => entity[key]).sort((a, b) => String(a).localeCompare(String(b)))
  );
  return currentEntitiesKeys === originalEntitiesKeys;
}

const compareCoOwners = (currentForm: Form, originalForm: Form) => {
  return compareUserEntitiesByKey(currentForm.formCoOwners, originalForm.formCoOwners, 'coOwnerEmail');
}

const compareUsers = (currentForm: Form, originalForm: Form) => {
  return compareUserEntitiesByKey(currentForm.formUsers, originalForm.formUsers, 'userGuid');
}

const compareLocales = (currentLocales: FormLocales[], originalLocales: FormLocales[]) => {
  if (currentLocales.length !== originalLocales.length) return false;

  // Sort locales by cultureId for consistent comparison
  const sortedCurrentLocales = [...currentLocales].sort((a, b) => a.cultureId - b.cultureId);
  const sortedOriginalLocales = [...originalLocales].sort((a, b) => a.cultureId - b.cultureId);

  return sortedCurrentLocales.every((currentLocale, index) => {
    const originalLocale = sortedOriginalLocales[index];
    return (
      currentLocale.cultureId === originalLocale.cultureId &&
      currentLocale.formId === originalLocale.formId &&
      currentLocale.title === originalLocale.title &&
      currentLocale.description === originalLocale.description
    );
  });
};

const areFormsEqual = (currentForm: Form, originalForm: Form) => {
  if (!currentForm || !originalForm) return false;

  // Compare basic form properties
  const basicPropertiesEqual = (
    currentForm.title === originalForm.title &&
    currentForm.description === originalForm.description &&
    currentForm.defaultLanguage === originalForm.defaultLanguage &&
    currentForm.enable === originalForm.enable &&
    compareCoOwners(currentForm, originalForm) &&
    compareUsers(currentForm, originalForm) &&
    compareLocales(currentForm.formLocales, originalForm.formLocales)
  );

  return basicPropertiesEqual;
};

const areRulesEqual = (currentRules: FormRule[], originalRules: FormRule[]) => {
  if (!currentRules || !originalRules) return !currentRules && !originalRules;
  if (currentRules.length !== originalRules.length) return false;

  const originalRulesMap = new Map(originalRules.map(rule => [rule.id, rule]));

  return currentRules.every(currentRule => {
    const originalRule = originalRulesMap.get(currentRule.id);
    if (!originalRule) return false;

    // Compare essential properties directly instead of using destructuring
    return (
      currentRule.name === originalRule.name &&
      currentRule.formId === originalRule.formId &&
      currentRule.orderIndex === originalRule.orderIndex &&
      currentRule.agent === originalRule.agent &&
      currentRule.logicalTest === originalRule.logicalTest &&
      currentRule.response === originalRule.response &&
      currentRule.action === originalRule.action &&
      currentRule.target === originalRule.target &&
      currentRule.targetType === originalRule.targetType &&
      isEqual(currentRule.targetOptions, originalRule.targetOptions)
    );
  });
};

const areSectionsEqual = (currentSections: Section[], originalSections: Section[]) => {
  if (!currentSections && !originalSections) return true;
  if (!currentSections || !originalSections) return false;
  if (currentSections.length !== originalSections.length) return false;

  return currentSections.every((currentSection, index) => {
    const originalSection = originalSections[index];
    if (!originalSection) return false;

    const { formFields: _, ...currentSectionWithoutFormFields } = currentSection;
    const { formFields: __, ...originalSectionWithoutFormFields } = originalSection;

    return isEqual(currentSectionWithoutFormFields, originalSectionWithoutFormFields);
  });
}

export const cleanFormAutosave = (formId: number | undefined) => {
  if (!formId) return;

  if (getOriginalFormFromLocalStorage(formId)) {
    storage.removeOriginalForm(formId);
  }

  if (getAutosavedFormFromLocalStorage(formId)) {
    storage.removeAutosaveForm(formId);
  }

  return true;
}

/**
 * Validates and cleans up the local storage by removing autosaved forms
 * that are no longer relevant based on the provided list of IDs. 
 * Used to check if the localStorage contains a deleted form. 
 *
 * @param ids - An array of IDs representing the forms that should be retained
 *              in the local storage. Any autosaved forms not in this list
 *              will be removed.
 */
export const validateAndCleanLocalStorage = (ids: number[]) => {
  const autoSaveForms = storage.getAutosaveForms();
  const autoSaveIds = autoSaveForms ? Object.keys(JSON.parse(autoSaveForms)) : [];

  const idsThatNeedToBeDeleted = autoSaveIds
    .map((id) => parseInt(id, 10))
    .filter((id) => !ids.includes(id));

  idsThatNeedToBeDeleted.forEach((id) => {
    cleanFormAutosave(id);
  });
};

const areFieldsEqual = (currentFieldList: Dictionary<CatalogItem[]>, originalFieldList: Dictionary<CatalogItem[]>) => {
  if (!currentFieldList || !originalFieldList) return true;

  const currentKeys = Object.keys(currentFieldList);
  const originalKeys = Object.keys(originalFieldList);

  if (currentKeys.length !== originalKeys.length) return false;

  for (const key of currentKeys) {
    const currentFieldItems = currentFieldList[key];
    const originalFieldItems = originalFieldList[key];

    if (!compareFieldListItems(currentFieldItems, originalFieldItems)) return false;
  }

  return true;
};

const compareFieldListItems = (currentFieldItem: CatalogItem[], originalFieldItem: CatalogItem[]) => {
  if (!originalFieldItem || currentFieldItem.length !== originalFieldItem.length) return false;

  for (let i = 0; i < currentFieldItem.length; i++) {
    if (!compareFields(currentFieldItem[i], originalFieldItem[i])) {
      return false;
    }
  }
  return true;
}

const compareFields = (currentField: CatalogItem, originalField: CatalogItem) => {
  if (!orderAndComparePreferences(currentField.preferences, originalField.preferences)) {
    return false;
  }
  
  const basicFieldPropertiesEqual = 
  currentField.description === originalField.description &&
  currentField.fieldId === originalField.fieldId &&
  currentField.index === originalField.index &&
  currentField.name === originalField.name &&
  currentField.score === originalField.score &&
  currentField.weight === originalField.weight &&
  isEqual(currentField.formFieldLocales, originalField.formFieldLocales);

  return basicFieldPropertiesEqual;
}
